import React, { useState, useEffect, useMemo } from 'react';
import {
  Stack,
  Text,
  TextField,
  Toggle,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
  Separator,
  Label,
  Checkbox,
  SpinnerSize,
  Spinner,
  Panel,
  PanelType,
  TooltipHost
} from '@fluentui/react';
import {
  DocumentRegular,
  ListRegular,
  SearchRegular,
  ShieldRegular,
  ClockRegular,
  DatabaseRegular,
  EyeRegular,
  InfoRegular,
  FolderRegular,
  ArrowRightRegular,
  WarningRegular,
  CheckmarkCircleRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import ScheduleConfigTab from './ScheduleConfigTab';
import styles from './SharePointConnectorSetup.module.css';

// ============================================================================
// SharePoint URL Parser - Parses various SharePoint URL formats
// ============================================================================

interface ParsedSharePointUrl {
  siteName: string;
  siteUrl: string;
  libraryName?: string;
  folderPath?: string;
  pathSegments: string[];
  displayPath: string;
  urlType: 'site' | 'site_with_library' | 'direct_folder';
  isValid: boolean;
  originalUrl: string;
}

/**
 * Parse a SharePoint URL and extract site, library, and folder information.
 * 
 * Supports:
 * 1. Site URL: https://tenant.sharepoint.com/sites/SiteName
 * 2. Folder URL with id param: .../AllItems.aspx?id=%2Fsites%2FSiteName%2FShared%20Documents%2FFolder
 * 3. Folder URL with RootFolder: .../AllItems.aspx?RootFolder=%2Fsites%2FSiteName%2FShared%20Documents%2FFolder
 */
function parseSharePointUrl(url: string): ParsedSharePointUrl {
  const result: ParsedSharePointUrl = {
    siteName: '',
    siteUrl: '',
    pathSegments: [],
    displayPath: '',
    urlType: 'site',
    isValid: false,
    originalUrl: url
  };

  if (!url || !url.trim()) {
    return result;
  }

  try {
    const decodedUrl = decodeURIComponent(url);
    const urlObj = new URL(decodedUrl.includes('://') ? decodedUrl : `https://${decodedUrl}`);
    const tenant = urlObj.hostname;

    // Extract query params
    const params = new URLSearchParams(urlObj.search);
    const idParam = params.get('id');
    const rootFolderParam = params.get('RootFolder');

    // Check for AllItems.aspx URL with id or RootFolder parameter (folder URLs)
    if (urlObj.pathname.includes('AllItems.aspx') && (idParam || rootFolderParam)) {
      const pathParam = idParam || rootFolderParam || '';
      const decodedPath = decodeURIComponent(pathParam);

      // Path looks like: /sites/SiteName/Shared Documents/Folder/Subfolder
      const siteMatch = decodedPath.match(/^\/sites\/([^\/]+)/);
      if (siteMatch) {
        const siteName = siteMatch[1];
        const sitePath = `/sites/${siteName}`;
        result.siteUrl = `https://${tenant}${sitePath}`;
        result.siteName = siteName;

        // Extract library and folder from remaining path
        const remainingPath = decodedPath.substring(sitePath.length).replace(/^\//, '');
        const parts = remainingPath.split('/').filter(p => p);

        if (parts.length > 0) {
          result.libraryName = parts[0];
          result.pathSegments = parts.slice(1);
          result.folderPath = result.pathSegments.join('/') || undefined;
          result.urlType = result.pathSegments.length > 0 ? 'direct_folder' : 'site_with_library';
        }

        result.isValid = true;
      }
    }
    // Check for sharing link format (:f: or :fl:)
    else if (url.includes('/:f:/') || url.includes('/:fl:/')) {
      const pathMatch = url.match(/\/:(f|fl):\/r(\/sites\/[^?]+)/);
      if (pathMatch) {
        const fullPath = decodeURIComponent(pathMatch[2]);
        const siteMatch = fullPath.match(/^\/sites\/([^\/]+)/);
        if (siteMatch) {
          result.siteName = siteMatch[1];
          result.siteUrl = `https://${tenant}/sites/${result.siteName}`;

          const remainingPath = fullPath.substring(`/sites/${result.siteName}`.length).replace(/^\//, '');
          const parts = remainingPath.split('/').filter(p => p);

          if (parts.length > 0) {
            result.libraryName = parts[0];
            result.pathSegments = parts.slice(1);
            result.folderPath = result.pathSegments.join('/') || undefined;
            result.urlType = result.pathSegments.length > 0 ? 'direct_folder' : 'site_with_library';
          }

          result.isValid = true;
        }
      }
    }
    // Simple site URL
    else {
      const siteMatch = urlObj.pathname.match(/^\/sites\/([^\/]+)/);
      if (siteMatch) {
        result.siteName = siteMatch[1];
        result.siteUrl = `https://${tenant}/sites/${result.siteName}`;
        result.urlType = 'site';
        result.isValid = true;
      }
    }

    // Build display path
    if (result.isValid) {
      const pathParts = [result.siteName];
      if (result.libraryName) pathParts.push(result.libraryName);
      if (result.pathSegments.length > 0) pathParts.push(...result.pathSegments);
      result.displayPath = pathParts.join(' → ');
    }
  } catch (e) {
    // Invalid URL
    result.isValid = false;
  }

  return result;
}

/**
 * Component to display parsed SharePoint URL path
 */
const SharePointPathDisplay: React.FC<{ parsedUrl: ParsedSharePointUrl }> = ({ parsedUrl }) => {
  if (!parsedUrl.isValid) return null;

  const isFolderSpecific = parsedUrl.urlType === 'direct_folder' || parsedUrl.urlType === 'site_with_library';

  return (
    <Stack tokens={{ childrenGap: 4 }} styles={{ 
      root: { 
        padding: '8px 12px', 
        backgroundColor: isFolderSpecific ? '#fff4ce' : '#f0f6ff', 
        borderRadius: 4,
        border: `1px solid ${isFolderSpecific ? '#c87e00' : '#0078d4'}`
      } 
    }}>
      {/* Path breadcrumb */}
      <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center" wrap>
        <FolderRegular style={{ color: isFolderSpecific ? '#c87e00' : '#0078d4', fontSize: 14 }} />
        {parsedUrl.siteName && (
          <>
            <Text variant="small" styles={{ root: { fontWeight: 600 } }}>{parsedUrl.siteName}</Text>
          </>
        )}
        {parsedUrl.libraryName && (
          <>
            <ArrowRightRegular style={{ fontSize: 10, color: '#666' }} />
            <Text variant="small">{parsedUrl.libraryName}</Text>
          </>
        )}
        {parsedUrl.pathSegments.map((segment, idx) => (
          <React.Fragment key={idx}>
            <ArrowRightRegular style={{ fontSize: 10, color: '#666' }} />
            <Text variant="small" styles={{ root: { fontWeight: idx === parsedUrl.pathSegments.length - 1 ? 600 : 400 } }}>
              {segment}
            </Text>
          </React.Fragment>
        ))}
      </Stack>

      {/* Crawl scope indicator */}
      <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
        {isFolderSpecific ? (
          <>
            <WarningRegular style={{ fontSize: 12, color: '#c87e00' }} />
            <Text variant="xSmall" styles={{ root: { color: '#8a6d3b' } }}>
              <strong>Folder-specific crawl:</strong> Only this folder and its subfolders will be crawled
            </Text>
          </>
        ) : (
          <>
            <CheckmarkCircleRegular style={{ fontSize: 12, color: '#0078d4' }} />
            <Text variant="xSmall" styles={{ root: { color: '#31708f' } }}>
              <strong>Full site crawl:</strong> All libraries in this site will be crawled
            </Text>
          </>
        )}
      </Stack>
    </Stack>
  );
};

interface SharePointConnectorSetupProps {
  isVisible: boolean;
  onDismiss: () => void;
  onComplete: (config: SharePointConfig) => void;
}

interface SharePointConfig {
  name: string;
  description: string;
  sourceUrls: string[]; // Array for multiple URLs - first one is primary
  libraries: string[];
  crawlSubsites: boolean;
  contentTypes: string[];
  fileTypes: string[];
  aclEnabled: boolean;
  crawlMode: 'full' | 'delta';
  processingMode: 'realtime' | 'batch'; // Add processing mode option
  enableVectorization: boolean;
  enableEmbeddings: boolean;
  // File age filtering
  maxFileAgeEnabled: boolean;
  maxFileAgeDays: number;
  // Advanced filtering options
  advancedFiltering: {
    enabled: boolean;
    filenameFilters: {
      contains: string[];
      patterns: string[];
      startsWidth: string[];
      exact: string[];
    };
    urlContentFilters: {
      mustContain: string[];
      cannotContain: string[];
    };
    regexPatterns: {
      include: string[];
      exclude: string[];
    };
    depthLimit: number;
    maxFileSize: number; // in MB
  };
  // Scheduling configuration
  schedule: {
    enabled: boolean;
    frequency: 'once' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';
    startTime?: string; // ISO datetime for first run
    timeOfDay?: string; // HH:MM format
    dayOfWeek?: number; // 0-6 for weekly
    dayOfMonth?: number; // 1-31 for monthly
    intervalMinutes?: number; // For hourly
    cronExpression?: string; // For custom
    timezone: string;
    endDate?: string; // Optional end date
    maxRuns?: number; // Maximum executions
    retryOnFailure: boolean;
    retryCount: number;
    retryDelayMinutes: number;
  };
  // Batch processing storage configuration
  storageContainerMode: 'existing' | 'new';
  storageContainerName?: string;
  newStorageContainerName?: string;
  // Index selection properties
  selectedIndexMode: 'existing' | 'new';
  selectedIndexName?: string;
  newIndexName?: string;
  // Optional properties for job results
  indexName?: string;
  jobId?: string;
  status?: string;
}

interface SearchIndex {
  id: string;
  name: string;
  connectorType: string;
  vectorEnabled: boolean;
  semanticEnabled: boolean;
  status: string;
  documentCount: number;
}

interface DiscoveryResult {
  totalSites: number;
  totalSubsites: number;
  estimatedDocuments: number;
  siteStructure: SiteInfo[];
  librariesFound: string[];
  fileTypeBreakdown: { [key: string]: number };
  contentTypeBreakdown: { [key: string]: number };
  discoveryTime: string;
}

interface SiteInfo {
  name: string;
  url: string;
  documentCount: number;
  subsites: SiteInfo[];
}

const SharePointConnectorSetup: React.FC<SharePointConnectorSetupProps> = ({
  isVisible,
  onDismiss,
  onComplete
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: MessageBarType } | null>(null);
  const [discoveryResult, setDiscoveryResult] = useState<DiscoveryResult | null>(null);

  // Index management state
  const [availableIndexes, setAvailableIndexes] = useState<SearchIndex[]>([]);
  const [loadingIndexes, setLoadingIndexes] = useState(false);
  
  // Storage container management state
  const [availableContainers, setAvailableContainers] = useState<string[]>([]);
  const [loadingContainers, setLoadingContainers] = useState(false);
  const [creatingIndex, setCreatingIndex] = useState(false);

  const [config, setConfig] = useState({
    siteUrl: '',
    clientId: '',
    clientSecret: '',
    tenantId: '',
    driveId: '',
    scheduleType: 'once',
    indexName: '',
    // SharePoint metadata properties
    name: '',
    description: '',
    sourceUrls: [''] as string[], // Start with one empty URL
    libraries: [] as string[],
    crawlSubsites: true, // Default to true as requested
    contentTypes: ['documents', 'lists', 'pages', 'news', 'events'] as string[], // Default to all content types
    fileTypes: ['pdf', 'doc', 'docx', 'xlsx', 'pptx', 'txt', 'html', 'msg'] as string[], // Default to all file types
    aclEnabled: true, // Default to true for security
    crawlMode: 'full' as 'full' | 'delta',
    processingMode: 'realtime' as 'realtime' | 'batch', // Default to realtime mode
    enableVectorization: true,
    enableEmbeddings: true,
    // File age filtering - default to 90 days
    maxFileAgeEnabled: false,
    maxFileAgeDays: 90,
    // Advanced filtering options
    advancedFiltering: {
      enabled: false,
      filenameFilters: {
        contains: [] as string[],
        patterns: [] as string[],
        startsWidth: [] as string[],
        exact: [] as string[]
      },
      urlContentFilters: {
        mustContain: [] as string[],
        cannotContain: [] as string[]
      },
      regexPatterns: {
        include: [] as string[],
        exclude: [] as string[]
      },
      depthLimit: 10,
      maxFileSize: 50 // MB
    },
    // Scheduling configuration
    schedule: {
      enabled: false,
      frequency: 'once' as 'once' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom',
      startTime: '',
      timeOfDay: '09:00',
      dayOfWeek: 1, // Monday
      dayOfMonth: 1,
      intervalMinutes: 60,
      cronExpression: '',
      timezone: 'UTC',
      endDate: '',
      maxRuns: undefined,
      retryOnFailure: true,
      retryCount: 3,
      retryDelayMinutes: 5
    },
    // Batch processing storage configuration
    storageContainerMode: 'existing' as 'existing' | 'new',
    storageContainerName: '',
    newStorageContainerName: '',
    selectedIndexMode: 'existing' as 'existing' | 'new',
    selectedIndexName: '',
    newIndexName: ''
  });

  // Helper functions for managing URLs - simplified approach
  const addUrl = () => {
    setConfig(prev => ({
      ...prev,
      sourceUrls: [...prev.sourceUrls, '']
    }));
  };

  const removeUrl = (index: number) => {
    setConfig(prev => ({
      ...prev,
      sourceUrls: prev.sourceUrls.filter((_, i) => i !== index)
    }));
  };

  const updateUrl = (index: number, newUrl: string) => {
    setConfig(prev => ({
      ...prev,
      sourceUrls: prev.sourceUrls.map((url, i) => i === index ? newUrl : url)
    }));
  };

  const contentTypeOptions: IDropdownOption[] = [
    { key: 'documents', text: 'Document Libraries', selected: true },
    { key: 'lists', text: 'SharePoint Lists', selected: true },
    { key: 'pages', text: 'Site Pages' },
    { key: 'news', text: 'News Articles' },
    { key: 'events', text: 'Calendar Events' }
  ];

  const fileTypeOptions: IDropdownOption[] = [
    { key: 'pdf', text: 'PDF (.pdf)', selected: true },
    { key: 'docx', text: 'Word Documents (.docx)', selected: true },
    { key: 'doc', text: 'Word Documents (.doc)', selected: true },
    { key: 'xlsx', text: 'Excel Spreadsheets (.xlsx)', selected: true },
    { key: 'pptx', text: 'PowerPoint Presentations (.pptx)', selected: true },
    { key: 'txt', text: 'Text Files (.txt)', selected: true },
    { key: 'html', text: 'HTML Files (.html)' },
    { key: 'msg', text: 'Outlook Messages (.msg)' }
  ];

  const crawlModeOptions: IDropdownOption[] = [
    { key: 'full', text: 'Full Crawl - Index all content' },
    { key: 'delta', text: 'Delta Crawl - Only changed content' }
  ];

  const processingModeOptions: IDropdownOption[] = [
    { key: 'realtime', text: 'Real-time Processing - Process documents immediately' },
    { key: 'batch', text: 'Batch Processing - Optimized for large datasets' }
  ];

  // Load available indexes when component mounts
  React.useEffect(() => {
    if (isVisible) {
      loadAvailableIndexes();
    }
  }, [isVisible]);

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return config.name && config.sourceUrls.length > 0 && config.sourceUrls[0].trim().length > 0;
      case 2:
        return (config.selectedIndexMode === 'existing' && config.selectedIndexName) ||
               (config.selectedIndexMode === 'new' && config.newIndexName);
      case 3:
        return true; // Authentication is validated via environment variables
      case 4:
        // Content filters are optional, but storage container is required for batch mode
        if (config.processingMode === 'batch') {
          return (config.storageContainerMode === 'existing' && config.storageContainerName) ||
                 (config.storageContainerMode === 'new' && config.newStorageContainerName);
        }
        return true; // For realtime mode, no additional validation needed
      case 5:
        return true; // Security & AI features are optional
      case 6:
        // Scheduling validation
        if (config.schedule.enabled) {
          if (!config.schedule.timezone) return false;
          if (config.schedule.frequency === 'custom' && !config.schedule.cronExpression) return false;
          if (config.schedule.frequency === 'weekly' && config.schedule.dayOfWeek === undefined) return false;
          if (config.schedule.frequency === 'monthly' && !config.schedule.dayOfMonth) return false;
          if (config.schedule.frequency === 'hourly' && !config.schedule.intervalMinutes) return false;
        }
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDiscovery = async () => {
    const urlsToDiscover = config.sourceUrls.filter(url => url.trim().length > 0);
    
    if (urlsToDiscover.length === 0 || !urlsToDiscover.some(url => url)) {
      setMessage({
        text: 'Please provide at least one SharePoint URL before running discovery',
        type: MessageBarType.warning
      });
      return;
    }

    setDiscoveryLoading(true);
    try {
      // Simple discovery request
      const discoveryRequest = {
        site_urls: urlsToDiscover.filter(url => url), // Remove empty URLs
        connector_type: 'sharepoint_online'
      };

      console.log('Discovery request:', discoveryRequest);

      const response = await authenticatedFetch('/api/connector-hub/discovery', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(discoveryRequest)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Discovery response:', data);

      if (data.success) {
        // Simple response parsing
        const sites = data.data?.sites || [];
        const libraries = data.data?.libraries || [];
        const discoveredUrls = data.data?.discovered_urls || [];
        
        // Calculate total library items (some may have counts)
        const totalLibraryItems = libraries.reduce((sum: number, lib: any) => {
          return sum + (lib.itemCount || 0);
        }, 0);
        
        // Group libraries by site URL to calculate per-site document counts
        const siteDocumentCounts: { [key: string]: number } = {};
        libraries.forEach((lib: any) => {
          // Extract site URL from library URL or use the discovered site URL
          const libUrl = lib.url || '';
          const siteUrl = urlsToDiscover.find(url => libUrl.includes(url)) || urlsToDiscover[0] || '';
          if (siteUrl) {
            siteDocumentCounts[siteUrl] = (siteDocumentCounts[siteUrl] || 0) + (lib.itemCount || 0);
          }
        });
        
        // Create simple result
        const result: DiscoveryResult = {
          totalSites: sites.length,
          totalSubsites: 0,
          estimatedDocuments: totalLibraryItems > 0 ? totalLibraryItems : 0, // Show actual counts if available
          librariesFound: libraries.map((lib: any) => lib.name || 'Unknown Library'),
          fileTypeBreakdown: {},
          contentTypeBreakdown: {},
          discoveryTime: new Date().toISOString(),
          siteStructure: sites.map((site: any) => {
            const siteUrl = site.webUrl || site.url || '';
            const documentCount = siteDocumentCounts[siteUrl] || totalLibraryItems || 0; // Use site-specific count or fallback to total
            return {
              name: site.name || 'SharePoint Site',
              url: siteUrl,
              documentCount: documentCount,
              subsites: []
            };
          })
        };
        
        setDiscoveryResult(result);
        setMessage({
          text: `Discovery completed: Found ${sites.length} sites, ${libraries.length} libraries${totalLibraryItems > 0 ? `, ${totalLibraryItems} items` : ''}`,
          type: MessageBarType.success
        });
      } else {
        throw new Error(data.error || 'Discovery failed');
      }
      
    } catch (error) {
      console.error('Discovery error:', error);
      setMessage({
        text: `Discovery failed: ${error}`,
        type: MessageBarType.error
      });
    } finally {
      setDiscoveryLoading(false);
    }
  };

  // Index management functions
  const loadAvailableIndexes = async () => {
    setLoadingIndexes(true);
    try {
      const response = await authenticatedFetch('/api/connector-hub/indexes');
      const data = await response.json();
      
      if (data.success) {
        setAvailableIndexes(data.indexes || []);
      } else {
        console.error('Failed to load indexes:', data.error);
        setMessage({
          text: 'Failed to load available indexes',
          type: MessageBarType.warning
        });
      }
    } catch (error) {
      console.error('Error loading indexes:', error);
      setMessage({
        text: 'Error loading available indexes',
        type: MessageBarType.error
      });
    } finally {
      setLoadingIndexes(false);
    }
  };

  // Storage container management functions
  const loadAvailableContainers = async () => {
    setLoadingContainers(true);
    try {
      const response = await authenticatedFetch('/api/connector-hub/containers');
      const data = await response.json();
      
      if (data.success) {
        // Handle both string array and object array (with name property) formats
        const containers = (data.containers || []).map((c: string | { name: string }) => 
          typeof c === 'string' ? c : c.name
        );
        setAvailableContainers(containers);
      } else {
        console.error('Failed to load storage containers:', data.error);
        setMessage({
          text: 'Failed to load available storage containers',
          type: MessageBarType.warning
        });
      }
    } catch (error) {
      console.error('Error loading storage containers:', error);
      setMessage({
        text: 'Error loading available storage containers',
        type: MessageBarType.error
      });
    } finally {
      setLoadingContainers(false);
    }
  };

  const createNewIndex = async (indexName: string) => {
    setCreatingIndex(true);
    try {
      const response = await authenticatedFetch('/api/connector-hub/indexes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: indexName,
          replace_existing: false
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessage({
          text: `Index "${indexName}" created successfully`,
          type: MessageBarType.success
        });
        // Reload indexes to include the new one
        await loadAvailableIndexes();
        return true;
      } else {
        setMessage({
          text: data.error || `Failed to create index "${indexName}"`,
          type: MessageBarType.error
        });
        return false;
      }
    } catch (error) {
      console.error('Error creating index:', error);
      setMessage({
        text: 'Error creating new index',
        type: MessageBarType.error
      });
      return false;
    } finally {
      setCreatingIndex(false);
    }
  };

  const handleComplete = async () => {
    const urlsToProcess = config.sourceUrls.filter(url => url.trim().length > 0);
    
    if (!config.name || urlsToProcess.length === 0 || !urlsToProcess.some(url => url)) {
      setMessage({
        text: 'Please fill in all required fields including at least one SharePoint URL',
        type: MessageBarType.error
      });
      return;
    }

    // Validate index selection
    if (config.selectedIndexMode === 'existing' && !config.selectedIndexName) {
      setMessage({
        text: 'Please select an existing index or choose to create a new one',
        type: MessageBarType.error
      });
      return;
    }

    if (config.selectedIndexMode === 'new' && !config.newIndexName) {
      setMessage({
        text: 'Please provide a name for the new index',
        type: MessageBarType.error
      });
      return;
    }

    setLoading(true);
    try {
      let indexName: string;

      // Step 1: Handle index selection (no creation here, job will handle it)
      if (config.selectedIndexMode === 'new') {
        // Use the new index name - job creation will handle creation
        indexName = config.newIndexName!;
      } else {
        // Use existing index
        indexName = config.selectedIndexName!;
        
        // Validate that the selected index exists and is SharePoint-compatible
        const selectedIndex = availableIndexes.find(idx => idx.name === indexName);
        if (selectedIndex && selectedIndex.connectorType !== 'sharepoint_online') {
          setMessage({
            text: `Selected index "${indexName}" is not a SharePoint index. Please select a SharePoint index or create a new one.`,
            type: MessageBarType.error
          });
          setLoading(false);
          return;
        }
      }

      // Step 2: Create SharePoint job using the main connector hub endpoint
      console.log('🔍 DEBUG: SharePoint job creation - config.contentTypes:', config.contentTypes);
      console.log('🔍 DEBUG: SharePoint job creation - config.fileTypes:', config.fileTypes);
      console.log('🔍 DEBUG: SharePoint job creation - config:', config);
      
      const jobPayload = {
        name: config.name,
        description: config.description,
        connectorTemplate: 'sharepoint_online',
        connectorType: 'sharepoint_online',
        targetIndex: indexName,
        createNewIndex: config.selectedIndexMode === 'new',  // Pass the createNewIndex flag
        content_types: config.contentTypes, // Add content_types at top level for orchestrator
        configuration: {
          site_url: config.sourceUrls[0] || '', // Keep for backward compatibility
          sourceUrls: config.sourceUrls.filter(url => url.trim().length > 0),
          site_urls: urlsToProcess, // New field for multiple URLs
          libraries: config.libraries,
          file_types: config.fileTypes.map(ft => ft.startsWith('.') ? ft : `.${ft}`),
          content_types: config.contentTypes, // Add content_types to configuration as well
          crawl_subsites: config.crawlSubsites,
          crawl_mode: config.crawlMode,
          processing_mode: config.processingMode,
          enable_embeddings: config.enableEmbeddings,
          enable_acl: config.aclEnabled,
          // File age filtering
          max_file_age_enabled: config.maxFileAgeEnabled,
          max_file_age_days: config.maxFileAgeEnabled ? config.maxFileAgeDays : null,
          // Advanced filtering configuration
          ...(config.advancedFiltering.enabled && {
            filename_filters: {
              contains: config.advancedFiltering.filenameFilters.contains,
              pattern: config.advancedFiltering.filenameFilters.patterns,
              starts_with: config.advancedFiltering.filenameFilters.startsWidth,
              exact: config.advancedFiltering.filenameFilters.exact
            },
            url_content_filters: {
              must_contain: config.advancedFiltering.urlContentFilters.mustContain,
              cannot_contain: config.advancedFiltering.urlContentFilters.cannotContain
            },
            regex_patterns: {
              include: config.advancedFiltering.regexPatterns.include,
              exclude: config.advancedFiltering.regexPatterns.exclude
            },
            depth_limit: config.advancedFiltering.depthLimit,
            max_file_size: config.advancedFiltering.maxFileSize * 1024 * 1024 // Convert MB to bytes
          }),
          // Storage container configuration for batch mode
          storage_container: config.processingMode === 'batch' 
            ? (config.storageContainerMode === 'existing' 
                ? config.storageContainerName 
                : config.newStorageContainerName)
            : undefined
          },
          schedule: {
            enabled: false,
            frequency: 'daily',
            time: '09:00',
            timezone: 'UTC'
          }
        };

      console.log('🔍 DEBUG: SharePoint job payload being sent:', jobPayload);
      console.log('🔍 DEBUG: SharePoint job payload.content_types:', jobPayload.content_types);
      console.log('🔍 DEBUG: SharePoint job payload.configuration.content_types:', jobPayload.configuration.content_types);

      const jobResponse = await authenticatedFetch('/api/connector-hub/jobs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jobPayload)
      });
      
      const jobResult = await jobResponse.json();
      
      if (!jobResult.success) {
        throw new Error(`Failed to create job: ${jobResult.error}`);
      }

      // Pass the job information back to the parent component (WITHOUT auto-starting)
      onComplete({
        ...config,
        indexName,
        jobId: jobResult.job.id,
        status: 'created' // Job is created but not started
      });
      
      // Close immediately without showing success popup
      onDismiss();
      
    } catch (error) {
      console.error('SharePoint setup error:', error);
      setMessage({
        text: `Failed to set up SharePoint connector: ${error}`,
        type: MessageBarType.error
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <Stack tokens={{ childrenGap: 12 }}>
      <Stack tokens={{ childrenGap: 6 }}>
        <Text variant="large" style={{ fontWeight: 600 }}>Basic Configuration</Text>
        <Text variant="medium" style={{ color: '#666' }}>
          Create a new SharePoint indexing job with custom settings
        </Text>
      </Stack>

      <TextField
        label="Connector Name"
        placeholder="e.g., Corporate SharePoint"
        value={config.name}
        onChange={(_, value) => setConfig(prev => ({ ...prev, name: value || '' }))}
        required
      />

      <TextField
        label="Description"
        placeholder="Brief description of this connector"
        value={config.description}
        onChange={(_, value) => setConfig(prev => ({ ...prev, description: value || '' }))}
        multiline
        rows={3}
      />

      {/* SharePoint URLs Management */}
      <Stack tokens={{ childrenGap: 8 }}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack tokens={{ childrenGap: 2 }}>
            <Label>SharePoint URLs</Label>
            <Text variant="xSmall" styles={{ root: { color: '#605e5c' } }}>
              Site URLs or folder-specific URLs (copies link from SharePoint)
            </Text>
          </Stack>
          <DefaultButton
            text="Add URL"
            iconProps={{ iconName: 'Add' }}
            onClick={addUrl}
          />
        </Stack>
        
        <Stack tokens={{ childrenGap: 12 }}>
          {config.sourceUrls.map((url, index) => {
            const parsedUrl = parseSharePointUrl(url);
            return (
              <Stack key={index} tokens={{ childrenGap: 6 }} styles={{ 
                root: { 
                  padding: '12px', 
                  border: '1px solid #e1e1e1', 
                  borderRadius: 4,
                  backgroundColor: '#fafafa'
                } 
              }}>
                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                  <TextField
                    label={index === 0 ? "Primary SharePoint URL" : `Additional URL ${index}`}
                    placeholder="https://company.sharepoint.com/sites/sitename or folder URL"
                    value={url}
                    onChange={(_, value) => updateUrl(index, value || '')}
                    required={index === 0}
                    styles={{ root: { flex: 1 } }}
                    description="Paste site URL or use 'Copy link' from SharePoint folder"
                  />
                  {config.sourceUrls.length > 1 && (
                    <DefaultButton
                      iconProps={{ iconName: 'Delete' }}
                      onClick={() => removeUrl(index)}
                      styles={{ root: { minWidth: '32px' } }}
                      title="Remove URL"
                    />
                  )}
                </Stack>
                
                {/* Show parsed path info */}
                {url.trim() && <SharePointPathDisplay parsedUrl={parsedUrl} />}
              </Stack>
            );
          })}
          
          {/* Summary of crawl targets */}
          {(() => {
            const validUrls = config.sourceUrls.filter(url => url.trim().length > 0);
            const parsedUrls = validUrls.map(parseSharePointUrl);
            const folderSpecific = parsedUrls.filter(p => p.urlType === 'direct_folder' || p.urlType === 'site_with_library').length;
            const fullSite = parsedUrls.filter(p => p.urlType === 'site').length;
            
            return validUrls.length > 0 && (
              <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { marginTop: 8 } }}>
                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                  <strong>Total targets:</strong> {validUrls.length}
                </Text>
                {fullSite > 0 && (
                  <Text variant="small" styles={{ root: { color: '#0078d4' } }}>
                    📁 {fullSite} full site{fullSite > 1 ? 's' : ''}
                  </Text>
                )}
                {folderSpecific > 0 && (
                  <Text variant="small" styles={{ root: { color: '#c87e00' } }}>
                    📂 {folderSpecific} folder-specific
                  </Text>
                )}
              </Stack>
            );
          })()}
        </Stack>
      </Stack>

      <TextField
        label="Document Libraries (Optional)"
        placeholder="e.g., Documents, Shared Documents, Reports (leave empty for all)"
        value={config.libraries.join(', ')}
        onChange={(_, value) => {
          const libraries = value ? value.split(',').map(lib => lib.trim()).filter(lib => lib) : [];
          setConfig(prev => ({ ...prev, libraries }));
        }}
        description="Comma-separated list of specific libraries to crawl. Leave empty to crawl all available libraries."
      />

      <Toggle
        label="Crawl Subsites"
        checked={config.crawlSubsites}
        onChange={(_, checked) => setConfig(prev => ({ ...prev, crawlSubsites: checked || false }))}
        onText="Enabled"
        offText="Disabled"
      />
    </Stack>
  );

  const renderStep2 = () => (
    <Stack tokens={{ childrenGap: 12 }}>
      <Stack tokens={{ childrenGap: 6 }}>
        <Text variant="large" style={{ fontWeight: 600 }}>Index Selection</Text>
        <Text variant="medium" style={{ color: '#666' }}>
          Choose an existing Azure Search index or create a new one for your SharePoint content
        </Text>
      </Stack>

      {loadingIndexes && (
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
          <Spinner size={SpinnerSize.small} />
          <Text variant="small">Loading available indexes...</Text>
        </Stack>
      )}

      <Stack tokens={{ childrenGap: 16 }}>
        <Label>Index Options</Label>
        
        <Checkbox
          label="Use existing index"
          checked={config.selectedIndexMode === 'existing'}
          onChange={(_, checked) => {
            if (checked) {
              setConfig(prev => ({ ...prev, selectedIndexMode: 'existing' }));
            }
          }}
        />
        
        {config.selectedIndexMode === 'existing' && (
          <Stack tokens={{ childrenGap: 12 }} style={{ marginLeft: '24px' }}>
            <Dropdown
              label="Select Index"
              placeholder="Choose an existing index"
              options={availableIndexes.map(index => ({
                key: index.name,
                text: `${index.name} (${index.documentCount.toLocaleString()} docs)`,
                disabled: false
              }))}
              selectedKey={config.selectedIndexName}
              onChange={(_, option) => {
                if (option) {
                  setConfig(prev => ({ ...prev, selectedIndexName: option.key as string }));
                }
              }}
              disabled={loadingIndexes || availableIndexes.length === 0}
              calloutProps={{
                preventDismissOnEvent: (ev) => ev.type === 'scroll' || ev.type === 'resize',
                styles: { calloutMain: { zIndex: 1000001 } }
              }}
            />
            
            {availableIndexes.length === 0 && !loadingIndexes && (
              <MessageBar messageBarType={MessageBarType.info}>
                No indexes found. You can create a new one below.
              </MessageBar>
            )}
            
            {config.selectedIndexName && (
              (() => {
                const selectedIndex = availableIndexes.find(idx => idx.name === config.selectedIndexName);
                return selectedIndex && selectedIndex.connectorType !== 'sharepoint_online' ? (
                  <MessageBar messageBarType={MessageBarType.warning}>
                    This index was not created for SharePoint. For best results, create a new SharePoint index.
                  </MessageBar>
                ) : null;
              })()
            )}
          </Stack>
        )}

        <Checkbox
          label="Create new index"
          checked={config.selectedIndexMode === 'new'}
          onChange={(_, checked) => {
            if (checked) {
              setConfig(prev => ({ 
                ...prev, 
                selectedIndexMode: 'new',
                newIndexName: prev.newIndexName || `sharepoint-${prev.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`
              }));
            }
          }}
        />
        
        {config.selectedIndexMode === 'new' && (
          <Stack tokens={{ childrenGap: 12 }} style={{ marginLeft: '24px' }}>
            <TextField
              label="New Index Name"
              placeholder="e.g., sharepoint-corporate-index"
              value={config.newIndexName}
              onChange={(_, value) => setConfig(prev => ({ ...prev, newIndexName: value || '' }))}
              description="Index name must be lowercase, contain only letters, numbers, and hyphens"
            />
            
            {creatingIndex && (
              <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                <Spinner size={SpinnerSize.small} />
                <Text variant="small">Creating index...</Text>
              </Stack>
            )}
            
            <MessageBar messageBarType={MessageBarType.info}>
              A new SharePoint-compatible index will be created with all required fields for documents, metadata, ACLs, and vector search.
            </MessageBar>
          </Stack>
        )}
      </Stack>
    </Stack>
  );

  const renderStep3 = () => (
    <Stack tokens={{ childrenGap: 12 }}>
      <Stack tokens={{ childrenGap: 6 }}>
        <Text variant="large" style={{ fontWeight: 600 }}>Authentication Configuration</Text>
        <Text variant="medium" style={{ color: '#666' }}>
          SharePoint authentication is configured via environment variables
        </Text>
      </Stack>

      <MessageBar messageBarType={MessageBarType.info}>
        <Text>
          The SharePoint connector uses Azure App Registration credentials configured in the server environment variables:
          <br />• SHAREPOINT_CLIENT_ID
          <br />• SHAREPOINT_CLIENT_SECRET  
          <br />• SHAREPOINT_TENANT_ID
          <br />
          <a href="https://docs.microsoft.com/en-us/sharepoint/dev/sp-add-ins/register-sharepoint-add-ins" target="_blank" rel="noopener noreferrer" style={{ marginLeft: '4px' }}>
            Learn more about Azure App Registration
          </a>
        </Text>
      </MessageBar>
    </Stack>
  );

  const renderStep4 = () => (
    <Stack tokens={{ childrenGap: 12 }}>
      <Stack tokens={{ childrenGap: 6 }}>
        <Text variant="large" style={{ fontWeight: 600 }}>Content & File Types</Text>
        <Text variant="medium" style={{ color: '#666' }}>
          Select what content and file types to include in the crawl
        </Text>
      </Stack>

      <Stack tokens={{ childrenGap: 16 }}>
        <Label>Content Types to Crawl</Label>
        {contentTypeOptions.map(option => (
          <Checkbox
            key={option.key}
            label={option.text}
            checked={config.contentTypes.includes(option.key as string)}
            onChange={(_, checked) => {
              const key = option.key as string;
              setConfig(prev => ({
                ...prev,
                contentTypes: checked
                  ? [...prev.contentTypes, key]
                  : prev.contentTypes.filter(type => type !== key)
              }));
            }}
          />
        ))}
      </Stack>

      <Stack tokens={{ childrenGap: 16 }}>
        <Label>File Types to Index</Label>
        <div className={styles.fileTypeGrid}>
          {fileTypeOptions.map(option => (
            <Checkbox
              key={option.key}
              label={option.text}
              checked={config.fileTypes.includes(option.key as string)}
              onChange={(_, checked) => {
                const key = option.key as string;
                setConfig(prev => ({
                  ...prev,
                  fileTypes: checked
                    ? [...prev.fileTypes, key]
                    : prev.fileTypes.filter(type => type !== key)
                }));
              }}
            />
          ))}
        </div>
      </Stack>

      {/* Advanced Filtering Section */}
      <Stack tokens={{ childrenGap: 8 }} styles={{ root: { border: '1px solid #e1e1e1', borderRadius: 4, padding: 16, backgroundColor: '#f9f9f9' } }}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack tokens={{ childrenGap: 2 }}>
            <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
              <SearchRegular style={{ fontSize: 14, color: '#0078d4' }} />
              <Text variant="medium" style={{ fontWeight: 600 }}>Advanced Filtering</Text>
            </Stack>
            <Text variant="small" style={{ color: '#666' }}>
              Fine-tune what content to include or exclude during crawling
            </Text>
          </Stack>
          <Toggle
            checked={config.advancedFiltering.enabled}
            onChange={(_, checked) => setConfig(prev => ({ 
              ...prev, 
              advancedFiltering: { ...prev.advancedFiltering, enabled: checked || false } 
            }))}
            onText="Enabled"
            offText="Disabled"
          />
        </Stack>

        {config.advancedFiltering.enabled && (
          <Stack tokens={{ childrenGap: 16 }} style={{ marginTop: '12px' }}>
            {/* Filename Filters */}
            <Stack tokens={{ childrenGap: 8 }}>
              <Text variant="medium" style={{ fontWeight: 600 }}>Filename Filters</Text>
              <TextField
                label="Must contain text"
                placeholder="e.g., report, policy, manual (comma-separated)"
                value={config.advancedFiltering.filenameFilters.contains.join(', ')}
                onChange={(_, value) => {
                  const contains = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                  setConfig(prev => ({
                    ...prev,
                    advancedFiltering: {
                      ...prev.advancedFiltering,
                      filenameFilters: { ...prev.advancedFiltering.filenameFilters, contains }
                    }
                  }));
                }}
                description="Files must contain at least one of these text patterns in their name"
              />
              <TextField
                label="Wildcard patterns"
                placeholder="e.g., *manual*, *2024*, User_* (comma-separated)"
                value={config.advancedFiltering.filenameFilters.patterns.join(', ')}
                onChange={(_, value) => {
                  const patterns = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                  setConfig(prev => ({
                    ...prev,
                    advancedFiltering: {
                      ...prev.advancedFiltering,
                      filenameFilters: { ...prev.advancedFiltering.filenameFilters, patterns }
                    }
                  }));
                }}
                description="Use * as wildcard. Example: *report* matches any file containing 'report'"
              />
              <TextField
                label="Must start with"
                placeholder="e.g., CORP_, DEPT_, User_ (comma-separated)"
                value={config.advancedFiltering.filenameFilters.startsWidth.join(', ')}
                onChange={(_, value) => {
                  const startsWidth = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                  setConfig(prev => ({
                    ...prev,
                    advancedFiltering: {
                      ...prev.advancedFiltering,
                      filenameFilters: { ...prev.advancedFiltering.filenameFilters, startsWidth }
                    }
                  }));
                }}
                description="Files must start with one of these prefixes"
              />
            </Stack>

            {/* URL Content Filters */}
            <Stack tokens={{ childrenGap: 8 }}>
              <Text variant="medium" style={{ fontWeight: 600 }}>URL/Path Filters</Text>
              <TextField
                label="URLs must contain"
                placeholder="e.g., Documents, Shared (comma-separated)"
                value={config.advancedFiltering.urlContentFilters.mustContain.join(', ')}
                onChange={(_, value) => {
                  const mustContain = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                  setConfig(prev => ({
                    ...prev,
                    advancedFiltering: {
                      ...prev.advancedFiltering,
                      urlContentFilters: { ...prev.advancedFiltering.urlContentFilters, mustContain }
                    }
                  }));
                }}
                description="Only crawl URLs containing these terms"
              />
              <TextField
                label="URLs cannot contain"
                placeholder="e.g., Forms, _vti_, SiteAssets, draft (comma-separated)"
                value={config.advancedFiltering.urlContentFilters.cannotContain.join(', ')}
                onChange={(_, value) => {
                  const cannotContain = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                  setConfig(prev => ({
                    ...prev,
                    advancedFiltering: {
                      ...prev.advancedFiltering,
                      urlContentFilters: { ...prev.advancedFiltering.urlContentFilters, cannotContain }
                    }
                  }));
                }}
                description="Skip URLs containing these terms"
              />
            </Stack>

            {/* Advanced Options */}
            <Stack tokens={{ childrenGap: 8 }}>
              <Text variant="medium" style={{ fontWeight: 600 }}>Advanced Options</Text>
              <Stack horizontal tokens={{ childrenGap: 16 }}>
                <TextField
                  label="Max crawl depth"
                  type="number"
                  value={config.advancedFiltering.depthLimit.toString()}
                  onChange={(_, value) => {
                    const depth = parseInt(value || '10');
                    if (!isNaN(depth) && depth > 0) {
                      setConfig(prev => ({
                        ...prev,
                        advancedFiltering: { ...prev.advancedFiltering, depthLimit: depth }
                      }));
                    }
                  }}
                  styles={{ root: { maxWidth: '120px' } }}
                  description="Max folder depth"
                />
                <TextField
                  label="Max file size (MB)"
                  type="number"
                  value={config.advancedFiltering.maxFileSize.toString()}
                  onChange={(_, value) => {
                    const size = parseInt(value || '50');
                    if (!isNaN(size) && size > 0) {
                      setConfig(prev => ({
                        ...prev,
                        advancedFiltering: { ...prev.advancedFiltering, maxFileSize: size }
                      }));
                    }
                  }}
                  styles={{ root: { maxWidth: '120px' } }}
                  description="Skip larger files"
                />
              </Stack>
              <TextField
                label="Exclude regex patterns"
                placeholder="e.g., ~\\$.*,.*backup.*,.*\\.tmp$ (comma-separated)"
                value={config.advancedFiltering.regexPatterns.exclude.join(', ')}
                onChange={(_, value) => {
                  const exclude = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                  setConfig(prev => ({
                    ...prev,
                    advancedFiltering: {
                      ...prev.advancedFiltering,
                      regexPatterns: { ...prev.advancedFiltering.regexPatterns, exclude }
                    }
                  }));
                }}
                description="Advanced regex patterns to exclude files"
              />
            </Stack>
          </Stack>
        )}
      </Stack>

      <Dropdown
        label="Crawl Mode"
        options={crawlModeOptions}
        selectedKey={config.crawlMode}
        onChange={(_, option) => setConfig(prev => ({ 
          ...prev, 
          crawlMode: option?.key as 'full' | 'delta' 
        }))}
        calloutProps={{
          preventDismissOnEvent: (ev) => ev.type === 'scroll' || ev.type === 'resize',
          styles: { calloutMain: { zIndex: 1000001 } }
        }}
      />

      <Dropdown
        label="Processing Mode"
        options={processingModeOptions}
        selectedKey={config.processingMode}
        onChange={(_, option) => {
          const mode = option?.key as 'realtime' | 'batch';
          setConfig(prev => ({ 
            ...prev, 
            processingMode: mode 
          }));
          
          // Load storage containers when batch mode is selected
          if (mode === 'batch') {
            loadAvailableContainers();
          }
        }}
        placeholder="Select processing mode"
        styles={{
          dropdownItemSelected: { backgroundColor: '#0078d4', color: 'white' },
          dropdownItem: { fontSize: 14 }
        }}
        calloutProps={{
          preventDismissOnEvent: (ev) => ev.type === 'scroll' || ev.type === 'resize',
          styles: { calloutMain: { zIndex: 1000001 } }
        }}
      />

      {/* Storage Container Configuration - Only show for batch mode */}
      {config.processingMode === 'batch' && (
        <Stack tokens={{ childrenGap: 8 }} styles={{ root: { border: '1px solid #e1e1e1', borderRadius: 4, padding: 16, backgroundColor: '#f9f9f9' } }}>
          <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
            <DatabaseRegular style={{ fontSize: 14, color: '#0078d4' }} />
            <Text variant="medium" style={{ fontWeight: 600 }}>Batch Processing Storage</Text>
          </Stack>
          <Text variant="small" style={{ color: '#666' }}>
            Azure Storage container for batch document processing
          </Text>
          
          <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="start">
            <Checkbox
              label="Use existing container"
              checked={config.storageContainerMode === 'existing'}
              onChange={(_, checked) => setConfig(prev => ({ 
                ...prev, 
                storageContainerMode: checked ? 'existing' : 'new' 
              }))}
            />
            <Checkbox
              label="Create new container"
              checked={config.storageContainerMode === 'new'}
              onChange={(_, checked) => setConfig(prev => ({ 
                ...prev, 
                storageContainerMode: checked ? 'new' : 'existing',
                newStorageContainerName: checked ? 
                  `sharepoint-${prev.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}` : ''
              }))}
            />
          </Stack>

          {config.storageContainerMode === 'existing' && (
            <Dropdown
              label="Select Storage Container"
              options={availableContainers.map(container => ({
                key: container,
                text: container
              }))}
              selectedKey={config.storageContainerName}
              onChange={(_, option) => setConfig(prev => ({ 
                ...prev, 
                storageContainerName: option?.key as string 
              }))}
              placeholder={loadingContainers ? "Loading containers..." : "Select a storage container"}
              disabled={loadingContainers || availableContainers.length === 0}
              calloutProps={{
                // Prevent dropdown from being dismissed when interacting with the Panel
                preventDismissOnEvent: (ev) => {
                  // Don't dismiss when clicking inside the dropdown
                  return ev.type === 'scroll' || ev.type === 'resize';
                },
                styles: {
                  // Ensure dropdown callout appears above Panel overlay
                  calloutMain: { zIndex: 1000001 }
                }
              }}
            />
          )}

          {availableContainers.length === 0 && !loadingContainers && config.storageContainerMode === 'existing' && (
            <MessageBar messageBarType={MessageBarType.warning}>
              No storage containers found. You may need to create one first.
            </MessageBar>
          )}

          {config.storageContainerMode === 'new' && (
            <TextField
              label="New Container Name"
              value={config.newStorageContainerName || ''}
              onChange={(_, value) => setConfig(prev => ({ 
                ...prev, 
                newStorageContainerName: value || '' 
              }))}
              placeholder="Enter container name (lowercase, alphanumeric)"
              description="Container name must be 3-63 characters, lowercase letters, numbers, and hyphens only"
            />
          )}
        </Stack>
      )}

      <Separator />

      {/* File Age Filter Section */}
      <Stack tokens={{ childrenGap: 8 }}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack tokens={{ childrenGap: 2 }}>
            <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
              <ClockRegular style={{ fontSize: 14, color: '#0078d4' }} />
              <Text variant="medium" style={{ fontWeight: 600 }}>File Age Filter</Text>
            </Stack>
            <Text variant="small" style={{ color: '#666' }}>
              Skip files older than specified age
            </Text>
          </Stack>
          <Toggle
            checked={config.maxFileAgeEnabled}
            onChange={(_, checked) => setConfig(prev => ({ ...prev, maxFileAgeEnabled: checked || false }))}
            onText="Enabled"
            offText="Disabled"
          />
        </Stack>

        {config.maxFileAgeEnabled && (
          <Stack tokens={{ childrenGap: 4 }}>
            <TextField
              label="Maximum File Age (days)"
              type="number"
              value={config.maxFileAgeDays.toString()}
              onChange={(_, newValue) => {
                const days = parseInt(newValue || '90');
                if (!isNaN(days) && days > 0) {
                  setConfig(prev => ({ ...prev, maxFileAgeDays: days }));
                }
              }}
              placeholder="90"
              suffix="days"
              styles={{ root: { maxWidth: '150px' } }}
            />
            <Text variant="small" style={{ color: '#666', fontSize: '11px' }}>
              Files older than {config.maxFileAgeDays} days will be ignored
            </Text>
          </Stack>
        )}
      </Stack>
    </Stack>
  );

  const renderStep5 = () => (
    <Stack tokens={{ childrenGap: 10 }}>
      <Stack tokens={{ childrenGap: 4 }}>
        <Text variant="large" style={{ fontWeight: 600 }}>Security & AI Features</Text>
        <Text variant="medium" style={{ color: '#666' }}>
          Configure security settings and AI-powered features
        </Text>
      </Stack>

      <Stack tokens={{ childrenGap: 8 }}>
        <Toggle
          label="Enable Access Control Lists (ACL)"
          checked={config.aclEnabled}
          onChange={(_, checked) => setConfig(prev => ({ ...prev, aclEnabled: checked || false }))}
          onText="Enabled"
          offText="Disabled"
        />
        <Text variant="small" style={{ color: '#666', marginTop: -4 }}>
          When enabled, search results will respect SharePoint permissions
        </Text>
      </Stack>

      <Separator />

      <Stack tokens={{ childrenGap: 8 }}>
        <Toggle
          label="Enable Vectorization"
          checked={config.enableVectorization}
          onChange={(_, checked) => setConfig(prev => ({ ...prev, enableVectorization: checked || false }))}
          onText="Enabled"
          offText="Disabled"
        />
        <Text variant="small" style={{ color: '#666', marginTop: -4 }}>
          Create vector embeddings for semantic search capabilities
        </Text>
      </Stack>

      <Stack tokens={{ childrenGap: 8 }}>
        <Toggle
          label="Enable AI Embeddings"
          checked={config.enableEmbeddings}
          onChange={(_, checked) => setConfig(prev => ({ ...prev, enableEmbeddings: checked || false }))}
          onText="Enabled"
          offText="Disabled"
          disabled={!config.enableVectorization}
        />
        <Text variant="small" style={{ color: '#666', marginTop: -4 }}>
          Use Azure OpenAI embeddings for enhanced search quality
        </Text>
      </Stack>

      <Separator />

      {/* Compact Discovery Section */}
      <Stack tokens={{ childrenGap: 10 }} style={{ 
        padding: '12px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '6px',
        border: '1px solid #e1e1e1'
      }}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack tokens={{ childrenGap: 2 }}>
            <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
              <SearchRegular style={{ fontSize: 16, color: '#0078d4' }} />
              <Text variant="medium" style={{ fontWeight: 600 }}>Discovery Preview</Text>
            </Stack>
            <Text variant="small" style={{ color: '#666' }}>
              Quick scan to validate configuration
            </Text>
          </Stack>
          <PrimaryButton
            text={discoveryLoading ? "Discovering..." : "🔍 Run Discovery"}
            iconProps={{ iconName: discoveryLoading ? undefined : 'Search' }}
            onClick={handleDiscovery}
            disabled={discoveryLoading || config.sourceUrls.filter(url => url.trim().length > 0).length === 0}
          />
        </Stack>
        
        {discoveryLoading && (
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" style={{
            padding: '8px',
            backgroundColor: '#fff',
            borderRadius: '4px',
            border: '1px solid #e1e1e1'
          }}>
            <Spinner size={SpinnerSize.small} />
            <Stack tokens={{ childrenGap: 2 }}>
              <Text variant="small" style={{ fontWeight: 500 }}>
                Analyzing SharePoint structure...
              </Text>
              <Text variant="small" style={{ color: '#666', fontSize: '10px' }}>
                This may take 10-30 seconds
              </Text>
            </Stack>
          </Stack>
        )}

        {!discoveryLoading && (
          <Stack tokens={{ childrenGap: 4 }} style={{
            padding: '8px',
            backgroundColor: '#fff',
            borderRadius: '4px',
            border: '1px solid #e1e1e1'
          }}>
            <Text variant="small" style={{ fontWeight: 500, color: '#323130' }}>
              💡 Discovery validates site access and shows structure
            </Text>
            <Stack tokens={{ childrenGap: 2 }}>
              <Text variant="small" style={{ color: '#666', fontSize: '11px' }}>
                • Verify SharePoint access • Count libraries • Preview structure
              </Text>
            </Stack>
            
            {config.sourceUrls.filter(url => url.trim().length > 0).length === 0 && (
              <Text variant="small" style={{ color: '#d83b01', fontStyle: 'italic', fontSize: '10px' }}>
                ⚠️ Please enter a SharePoint URL in Step 1
              </Text>
            )}
          </Stack>
        )}

        {discoveryResult && (
          <Stack tokens={{ childrenGap: 12 }} style={{
            padding: '12px',
            backgroundColor: '#f0f9ff',
            borderRadius: '6px',
            border: '1px solid #0078d4'
          }}>
            <Stack horizontal tokens={{ childrenGap: 2 }} verticalAlign="center">
              <Text variant="small" style={{ fontWeight: 600, color: '#0078d4' }}>
                ✅ Discovery Complete
              </Text>
            </Stack>
            
            {/* Summary Statistics */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center', padding: '8px' }}>
                <Text variant="large" style={{ fontWeight: 600, color: '#0078d4', display: 'block' }}>
                  {discoveryResult.totalSites}
                </Text>
                <Text variant="small" style={{ color: '#666' }}>Sites</Text>
              </div>
              <div style={{ textAlign: 'center', padding: '8px' }}>
                <Text variant="large" style={{ fontWeight: 600, color: '#107c10', display: 'block' }}>
                  {discoveryResult.estimatedDocuments > 0 ? discoveryResult.estimatedDocuments.toLocaleString() : '?'}
                </Text>
                <Text variant="small" style={{ color: '#666' }}>Library Items</Text>
              </div>
              <div style={{ textAlign: 'center', padding: '8px' }}>
                <Text variant="large" style={{ fontWeight: 600, color: '#d83b01', display: 'block' }}>
                  {discoveryResult.librariesFound.length}
                </Text>
                <Text variant="small" style={{ color: '#666' }}>Libraries</Text>
              </div>
            </div>

            {/* Libraries Found */}
            {discoveryResult.librariesFound.length > 0 && (
              <Stack tokens={{ childrenGap: 6 }}>
                <Text variant="small" style={{ fontWeight: 600 }}>📁 Libraries Found:</Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {discoveryResult.librariesFound.slice(0, 5).map((library, index) => (
                    <div key={index} style={{
                      padding: '2px 8px',
                      backgroundColor: '#fff4e6',
                      borderRadius: '12px',
                      fontSize: '10px'
                    }}>
                      {library}
                    </div>
                  ))}
                  {discoveryResult.librariesFound.length > 5 && (
                    <div style={{
                      padding: '2px 8px',
                      backgroundColor: '#f3f2f1',
                      borderRadius: '12px',
                      fontSize: '10px',
                      color: '#666'
                    }}>
                      +{discoveryResult.librariesFound.length - 5} more
                    </div>
                  )}
                </div>
              </Stack>
            )}

            {/* Site Structure - Compact */}
            {discoveryResult.siteStructure.length > 0 && (
              <Stack tokens={{ childrenGap: 6 }}>
                <Text variant="small" style={{ fontWeight: 600 }}>🏗️ Site Structure:</Text>
                {discoveryResult.siteStructure.map(site => (
                  <div key={site.url} style={{
                    padding: '6px 8px',
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    border: '1px solid #e1e1e1'
                  }}>
                    <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
                      <DocumentRegular style={{ fontSize: 14, color: '#0078d4' }} />
                      <Stack tokens={{ childrenGap: 1 }} style={{ flex: 1 }}>
                        <Text variant="small" style={{ fontWeight: 500 }}>{site.name}</Text>
                        <Text variant="small" style={{ color: '#666', fontSize: '10px' }}>
                          {site.documentCount.toLocaleString()} documents
                        </Text>
                      </Stack>
                    </Stack>
                  </div>
                ))}
              </Stack>
            )}

            <Text variant="small" style={{ color: '#666', fontStyle: 'italic', fontSize: '10px' }}>
              Discovery completed at: {discoveryResult.discoveryTime ? 
                new Date(discoveryResult.discoveryTime).toLocaleString() : 
                new Date().toLocaleString()}
            </Text>
          </Stack>
        )}
      </Stack>
    </Stack>
  );

  const renderStep6 = () => (
    <ScheduleConfigTab
      schedule={config.schedule as any}
      onChange={(schedule: any) => setConfig({ ...config, schedule: { ...config.schedule, ...schedule } })}
    />
  );

  const renderStepIndicator = () => (
    <div className={styles.stepIndicator}>
      {[1, 2, 3, 4, 5, 6].map(step => (
        <div
          key={step}
          className={`${styles.step} ${step <= currentStep ? styles.active : ''}`}
        >
          <div className={styles.stepNumber}>{step}</div>
          <Text variant="small" className={styles.stepLabel}>
            {step === 1 && 'Basic Config'}
            {step === 2 && 'Index Selection'}
            {step === 3 && 'Authentication'}
            {step === 4 && 'Content Types'}
            {step === 5 && 'Security & AI'}
            {step === 6 && 'Scheduling'}
          </Text>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Panel
        isOpen={isVisible}
        onDismiss={onDismiss}
        headerText="Create SharePoint Job"
        type={PanelType.large}
        className={styles.setupPanel}
        isLightDismiss={false}
        hasCloseButton={true}
        onRenderNavigationContent={() => null}
      >
        <Stack tokens={{ childrenGap: 16 }}>
          {renderStepIndicator()}

          {message && (
            <MessageBar
              messageBarType={message.type}
              onDismiss={() => setMessage(null)}
            >
              {message.text}
            </MessageBar>
          )}

          <div className={styles.stepContent}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}
            {currentStep === 6 && renderStep6()}
          </div>

          <Stack horizontal horizontalAlign="space-between" className={styles.navigationButtons}>
            <DefaultButton
              text="Previous"
              onClick={handlePrevious}
              disabled={currentStep === 1}
            />
            
            <Stack horizontal tokens={{ childrenGap: 12 }}>
              <DefaultButton text="Cancel" onClick={onDismiss} />
              {currentStep === 6 ? (
                <PrimaryButton
                  text={loading ? "Creating..." : "Create Connector"}
                  onClick={handleComplete}
                  disabled={loading}
                />
              ) : (
                <PrimaryButton
                  text="Next"
                  onClick={handleNext}
                  disabled={!isStepValid()}
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Panel>
    </>
  );
};

export default SharePointConnectorSetup;
